/**
 * markTestCascade.tsx — what the mark-test cascade DID, said out loud.
 *
 * Owner repo: @thegpservice/gpsclinics-portal. Slice: pipeline / test records.
 *
 * ══════════════════════════════════════════════════════════════════════════
 * ⚠️ THE DEFECT THIS MODULE EXISTS TO CLOSE (2026-09-06).
 * ══════════════════════════════════════════════════════════════════════════
 *
 * One press of "Mark as test record" writes THREE records: the deal, the plan
 * behind it and the patient behind it. clinic-service has reported all three
 * back as `{deal, cascade}` since 2026-09-04 — six per-leg outcomes and a
 * ready-written operator sentence for four of them — and headed the controller
 * docblock "WHAT THE PORTAL MUST RENDER".
 *
 * The portal typed the call `Promise<DealCard>` and threw the body away. So the
 * three outcomes that most need a human — a WITHHELD patient leg, a FAILED
 * plan leg, an ANOMALY — closed the dialog on an unqualified success over a
 * HALF-MARKED record. The server's "never silent" guarantee was true of the
 * wire and false of the product, which is the worse of the two: an operator who
 * is told nothing concludes the job is done.
 *
 * ── WHY A SECOND DIALOG AND NOT AN INLINE LINE IN THE FIRST ────────────────
 * The confirm dialog is a QUESTION and this is an ANSWER, and the two must not
 * wear the same chrome — ConfirmDialog's second button is hard-labelled
 * "Cancel", which after a committed write reads as "undo it" and cannot be
 * offered. It also has to be impossible to walk past: a successful mark makes
 * the card vanish from the default board, so the operator's next move is to
 * close the drawer — and a line rendered inside that drawer is exactly the
 * thing they are about to dismiss without reading. This report is modal, it
 * outlives the refetch behind it, and it is dismissed deliberately.
 *
 * ── WHAT THIS MODULE MAY AND MAY NOT DECIDE ────────────────────────────────
 * It renders the server's judgement; it does not second-guess it. In
 * particular it NEVER re-derives whether work is outstanding from the outcome
 * words: a `stub` leg is outstanding on stage and finished on a laptop, and
 * only the server knows which runtime it is. `cascade.complete` is the one
 * flag that answers "is anything left to do", and it is the only thing the
 * re-run offer is keyed on.
 *
 * The `detail` sentences are rendered VERBATIM. The server's contract commits
 * to whole, correctly-pluralised, PHI-free sentences precisely so that no
 * client has to parse a count back out of one or repair its grammar.
 */
import type { CSSProperties } from 'react';
import type { DealTestCascade, DealTestCascadeLeg, DealTestCascadeOutcome } from '../api/types';
import { DialogShell } from '../ui/ConfirmDialog';
import { Button, Help } from '../ui/kit';
import { DealLinkButton } from './duplicateSurface';

/** Which way the press was going. The status words differ; nothing else does. */
export type CascadeDirection = 'mark' | 'unmark';

/**
 * The two outcomes with NOTHING to tell anybody: the leg did what was asked, or
 * there was nothing on that side at all. Every other outcome carries a `detail`
 * sentence from the server and therefore a reason the operator is being shown
 * this at all.
 */
const QUIET_OUTCOMES: ReadonlySet<DealTestCascadeOutcome> = new Set<DealTestCascadeOutcome>([
  'done',
  'skipped',
]);

/**
 * Does this cascade have anything to say?
 *
 * ⚠️ AN ABSENT CASCADE IS `false`, AND THAT IS NOT THE SAME CLAIM AS A CLEAN
 * ONE. A clinic-service that predates 2026-09-04 answers `mark-test` with a
 * bare card and cannot tell us what the other two records did. Reporting
 * "everything worked" there would be inventing an observation; reporting "we
 * could not check" on every single mark would be noise on the estate's own
 * deployed servers, which all send it. So an absent cascade restores exactly
 * the pre-cascade behaviour — silence — and the type keeps the key optional so
 * no caller can accidentally read `.plan` off it.
 *
 * `!complete` is checked FIRST and independently of the outcome words, because
 * completeness is the server's judgement and can turn on things this client
 * cannot see (a `stub` is outstanding on a deployed runtime and finished on a
 * laptop). Never re-derive it here.
 */
export function cascadeIsEventful(cascade?: DealTestCascade): boolean {
  if (!cascade) return false;
  if (!cascade.complete) return true;
  return !QUIET_OUTCOMES.has(cascade.plan.outcome) || !QUIET_OUTCOMES.has(cascade.patient.outcome);
}

/**
 * The three visual registers, and `notice` is the one that matters.
 *
 * `withheld` is NOT `warn`. It means the clinical-safety rule fired: the
 * patient behind this deal has other live journeys, so their account was
 * deliberately left visible for a clinician to find. Painting that red teaches
 * operators that the safety rule is a malfunction and, worse, spends the red on
 * a state nobody needs to act on urgently — leaving nothing left to signal the
 * `failed` leg sitting next to it.
 */
type LegTone = 'ok' | 'notice' | 'warn';

const TONE_OF: Record<DealTestCascadeOutcome, LegTone> = {
  done: 'ok',
  skipped: 'ok',
  withheld: 'notice',
  stub: 'warn',
  anomaly: 'warn',
  failed: 'warn',
};

/**
 * The status word for each outcome, per direction.
 *
 * ⚠️ EXHAUSTIVE `Record`s, NOT PARTIALS, and that is load-bearing on a union
 * that arrives OFF THE WIRE. A missing member would not fall back to anything
 * sensible — it would render the literal word "undefined" as the status of a
 * money record. Widening `DealTestCascadeOutcome` is now a compile error here
 * until somebody decides what the new outcome is called in front of staff.
 */
const STATUS_WORD: Record<CascadeDirection, Record<DealTestCascadeOutcome, string>> = {
  mark: {
    done: 'Marked',
    skipped: 'Nothing to mark',
    withheld: 'Deliberately left alone',
    stub: 'Not marked — no service configured',
    anomaly: 'Could not be identified',
    failed: 'Not marked — this failed',
  },
  unmark: {
    done: 'Unmarked',
    skipped: 'Nothing to unmark',
    withheld: 'Deliberately left alone',
    stub: 'Not unmarked — no service configured',
    anomaly: 'Could not be identified',
    failed: 'Not unmarked — this failed',
  },
};

/** What each leg IS, in words an operator recognises from the board. */
const LEG_NAME = {
  plan: 'The plan and its payments',
  patient: 'The patient account',
} as const;

export type CascadeLegKey = keyof typeof LEG_NAME;

/** One renderable row. Pure, so the wording is testable without a DOM. */
export interface CascadeLegView {
  key: CascadeLegKey;
  name: string;
  status: string;
  tone: LegTone;
  detail: string | null;
  relatedDealIds: string[];
}

export function cascadeLegView(
  key: CascadeLegKey,
  leg: DealTestCascadeLeg,
  direction: CascadeDirection,
): CascadeLegView {
  return {
    key,
    name: LEG_NAME[key],
    status: STATUS_WORD[direction][leg.outcome],
    tone: TONE_OF[leg.outcome],
    detail: leg.detail,
    // `?? []` because the key is defaulted on the server but ABSENT on any
    // clinic-service older than 2026-09-06 — the sentence still renders, it
    // just has no links under it.
    relatedDealIds: leg.relatedDealIds ?? [],
  };
}

/**
 * THE HEADLINE ALWAYS LEADS WITH WHAT DID HAPPEN.
 *
 * The deal write is the transaction and both legs are post-commit, so by the
 * time this renders the deal genuinely IS marked — that is also why the server
 * answers 200 on an incomplete cascade. Leading with the failure would tell the
 * operator the opposite of the one thing that is certainly true, and send them
 * to re-press an action that has already landed.
 */
export function cascadeHeadline(dealName: string, direction: CascadeDirection): string {
  return direction === 'mark'
    ? `${dealName} is marked as a test record.`
    : `${dealName} is no longer marked as a test record.`;
}

const TONE_COLOUR: Record<LegTone, string> = {
  ok: 'var(--gps-muted)',
  notice: 'var(--gps-text)',
  warn: 'var(--gps-danger-text)',
};

const rowStyle: CSSProperties = {
  border: '1px solid var(--gps-border)',
  borderRadius: 'var(--gps-r-md)',
  padding: '10px 12px',
  display: 'grid',
  gap: '4px',
};

/**
 * The report. Rendered after the write, never instead of it.
 *
 * `onRerun` is offered ONLY on `complete: false` — re-posting the identical
 * request is the server's own documented way to finish an incomplete cascade
 * (every leg is idempotent), and offering it on a complete one would invite
 * operators to re-press a finished action looking for a greener answer.
 */
export function MarkTestCascadeDialog({
  dealName,
  direction,
  cascade,
  busy = false,
  error,
  onRerun,
  onClose,
  onOpenDeal,
}: {
  dealName: string;
  direction: CascadeDirection;
  cascade: DealTestCascade;
  busy?: boolean;
  /** A failed re-run. The report itself stays on screen underneath it. */
  error?: string | null;
  onRerun: () => void;
  onClose: () => void;
  /** Opens one of the `withheld` leg's related deals. Absent ⇒ ids are listed, not linked. */
  onOpenDeal?: (dealId: string) => void;
}) {
  const legs = [
    cascadeLegView('plan', cascade.plan, direction),
    cascadeLegView('patient', cascade.patient, direction),
  ];
  const title = direction === 'mark' ? 'Marked as a test record' : 'Unmarked as a test record';

  return (
    <DialogShell title={title} onCancel={busy ? () => {} : onClose} width="min(520px, 100%)">
      {/* `role="status"` and not `alert`: the deal was marked successfully, and
          an assertive interruption would announce a success as an emergency.
          The one genuinely bad state — a failed RE-RUN — gets its own alert. */}
      <div role="status" style={{ color: 'var(--gps-text)', fontSize: '14.5px' }}>
        <p style={{ margin: '0 0 var(--gps-s-2)' }}>{cascadeHeadline(dealName, direction)}</p>
        {cascade.complete ? null : (
          <p style={{ margin: '0 0 var(--gps-s-2)', fontWeight: 600 }}>
            Some of it did not finish, so this record is only part-marked. Re-running finishes just the
            outstanding parts — nothing already done is repeated.
          </p>
        )}
        <ul aria-label="What else was marked" style={{ listStyle: 'none', margin: 0, padding: 0, display: 'grid', gap: '8px' }}>
          {legs.map((leg) => (
            <li key={leg.key} style={rowStyle}>
              <span style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', alignItems: 'baseline' }}>
                <strong style={{ fontSize: '13.5px' }}>{leg.name}</strong>
                {/* The tone is never the only carrier of the state (WCAG 1.4.1):
                    the status word says it in full, and the sentence under it
                    says why. */}
                <span style={{ fontSize: '13px', color: TONE_COLOUR[leg.tone] }}>{leg.status}</span>
              </span>
              {leg.detail ? (
                <span style={{ fontSize: '13px', color: 'var(--gps-text)', lineHeight: 1.45 }}>{leg.detail}</span>
              ) : null}
              {leg.relatedDealIds.length > 0 ? (
                <span style={{ display: 'flex', flexWrap: 'wrap', gap: '10px', alignItems: 'baseline' }}>
                  {/* THE SENTENCE ABOVE ENDS IN AN INSTRUCTION ("mark those
                      deals too"), and these are the only route to the cards it
                      means — the leg joins on the patient, the drawer's sibling
                      list joins on the contact, so they are routinely in no list
                      the operator can already see. Numbered rather than named:
                      the server sends opaque ids and nothing else, which is the
                      point (R19), so inventing a label would be inventing a
                      fact. */}
                  <span style={{ fontSize: '12.5px', color: 'var(--gps-muted)' }}>
                    {leg.relatedDealIds.length === 1 ? 'The other deal:' : 'The other deals:'}
                  </span>
                  {leg.relatedDealIds.map((dealId, index) =>
                    onOpenDeal ? (
                      <DealLinkButton
                        key={dealId}
                        onClick={() => onOpenDeal(dealId)}
                        label={`Open the other deal (${index + 1} of ${leg.relatedDealIds.length})`}
                      >
                        Open deal {index + 1}
                      </DealLinkButton>
                    ) : (
                      <span key={dealId} style={{ fontSize: '12.5px', color: 'var(--gps-muted)' }}>
                        Deal {index + 1}
                      </span>
                    ),
                  )}
                </span>
              ) : null}
            </li>
          ))}
        </ul>
        {cascade.complete ? null : (
          <Help style={{ marginTop: 'var(--gps-s-2)', marginBottom: 0 }}>
            If re-running keeps failing, raise it with support rather than leaving it — a part-marked
            record keeps counting as real on the reporting boards.
          </Help>
        )}
      </div>
      {error ? (
        <span role="alert" style={{ display: 'block', marginTop: 'var(--gps-s-2)', color: 'var(--gps-danger-text)', fontSize: '13.5px' }}>
          {error}
        </span>
      ) : null}
      <div style={{ display: 'flex', justifyContent: 'flex-start', gap: '8px', marginTop: 'var(--gps-s-3)' }}>
        {cascade.complete ? null : (
          <Button
            type="button"
            variant="primary"
            disabled={busy}
            onClick={onRerun}
            style={{ width: 'auto', minHeight: '44px', padding: '10px 18px', fontSize: '14.5px' }}
          >
            {busy ? 'Re-running…' : 'Re-run the unfinished parts'}
          </Button>
        )}
        <Button
          type="button"
          variant={cascade.complete ? 'primary' : 'ghost'}
          disabled={busy}
          onClick={onClose}
          style={{ width: 'auto', minHeight: '44px', padding: '10px 18px', fontSize: '14.5px' }}
        >
          Done
        </Button>
      </div>
    </DialogShell>
  );
}
